import { useState } from 'react'
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { IMontagemGrupoProduto } from '../../Pages/HCP/MontagemCartelas/montagem-cartelas.hook'

interface IMontagemCartelasTableProps {
  grupos: IMontagemGrupoProduto[]
  loading: boolean
}

function StatusChip({ grupo }: { grupo: IMontagemGrupoProduto }) {
  if (grupo.podeFecharCartela) {
    return (
      <Chip
        label="Pronto para fechar"
        size="small"
        sx={{ bgcolor: 'rgba(132, 168, 2, 0.15)', color: '#4a6000', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  if (grupo.meiaCartelaDisponivel) {
    const label = grupo.qtdMeiaCartelas === 1
      ? 'Meia cartela disponível'
      : `${grupo.qtdMeiaCartelas}× meia cartela`
    return (
      <Chip
        label={label}
        size="small"
        sx={{ bgcolor: 'rgba(2, 136, 209, 0.12)', color: '#01579b', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  const faltam = grupo.minimoTiragem - grupo.totalTiragem
  return (
    <Chip
      label={`Aguardando ${faltam.toLocaleString('pt-BR')} un`}
      size="small"
      sx={{ bgcolor: 'rgba(255, 193, 7, 0.15)', color: '#7a5800', fontWeight: 600, fontSize: '0.75rem' }}
    />
  )
}

function GrupoRow({ grupo }: { grupo: IMontagemGrupoProduto }) {
  const [open, setOpen] = useState(false)

  const rowBg = grupo.podeFecharCartela
    ? 'rgba(132, 168, 2, 0.05)'
    : grupo.meiaCartelaDisponivel
    ? 'rgba(2, 136, 209, 0.04)'
    : 'inherit'

  const rowHover = grupo.podeFecharCartela
    ? 'rgba(132, 168, 2, 0.10)'
    : grupo.meiaCartelaDisponivel
    ? 'rgba(2, 136, 209, 0.08)'
    : 'rgba(0,0,0,0.02)'

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: rowBg, '&:hover': { bgcolor: rowHover } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography fontWeight={600} fontSize={14}>{grupo.produto_nome}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14}>{grupo.itens.length}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontWeight={600} fontSize={14}>
            {grupo.totalTiragem.toLocaleString('pt-BR')}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14} color="text.secondary">
            {grupo.minimoTiragem.toLocaleString('pt-BR')}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14} color="text.secondary">
            {grupo.meiaCartela ? grupo.meiaCartela.toLocaleString('pt-BR') : '—'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <StatusChip grupo={grupo} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>#Pedido</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Cliente</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Tiragem</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Designer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Arte Final</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Pend. pedido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grupo.itens.map((item) => (
                    <TableRow key={item.produto_pedido_id} hover>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{item.numero}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{item.cliente}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                        {(item.tiragem ?? (item.producao?.tiragem ? parseInt(item.producao.tiragem, 10) : 0)).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        {item.designer?.nome ?? '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {item.link_arte_final ? (
                          <Link href={item.link_arte_final} target="_blank" rel="noopener noreferrer" underline="hover">
                            Arte Final
                          </Link>
                        ) : (
                          <Typography component="span" fontSize="0.85rem" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.itens_pendentes === 0 ? 'Completo' : `${item.itens_pendentes} pend.`}
                          size="small"
                          sx={{
                            fontSize: '0.70rem', height: 20, fontWeight: 600,
                            bgcolor: item.itens_pendentes === 0 ? 'rgba(46,125,50,0.12)' : '#fff3e0',
                            color: item.itens_pendentes === 0 ? '#2e7d32' : '#e65100',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function MontagemCartelasTable({ grupos, loading }: IMontagemCartelasTableProps) {
  if (!loading && grupos.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary">Nenhum produto encontrado.</Typography>
      </Box>
    )
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}>
            <TableCell sx={{ width: 48 }} />
            <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Produto</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Pedidos</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Tiragem Acumulada</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Mínimo Cartela</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Meia Cartela</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {grupos.map((grupo) => (
            <GrupoRow key={grupo.produto_nome} grupo={grupo} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
